import { useState } from 'react';
import { X, Plus, Zap, Clock, Calendar, Webhook, Play, Mail, Database, Code, FileText, ArrowRight } from 'lucide-react';

interface AutomationBuilderProps {
  onClose: () => void;
  onSave: (automation: {
    name: string;
    description: string;
    trigger: { type: string; label: string };
    actions: Array<{ type: string; label: string }>;
  }) => void;
}

export default function AutomationBuilder({ onClose, onSave }: AutomationBuilderProps) {
  const [step, setStep] = useState<'basic' | 'trigger' | 'actions'>('basic');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTrigger, setSelectedTrigger] = useState<{ type: string; label: string } | null>(null);
  const [selectedActions, setSelectedActions] = useState<Array<{ type: string; label: string }>>([]);

  const triggers = [
    { type: 'schedule', label: 'On Schedule', icon: Clock, description: 'Run at specific times or intervals' },
    { type: 'event', label: 'On Event', icon: Zap, description: 'Trigger when something happens' },
    { type: 'webhook', label: 'On Webhook', icon: Webhook, description: 'HTTP webhook trigger' },
    { type: 'manual', label: 'Manual Trigger', icon: Play, description: 'Run manually when needed' }
  ];

  const availableActions = [
    { type: 'email', label: 'Send Email', icon: Mail },
    { type: 'database', label: 'Update Database', icon: Database },
    { type: 'api', label: 'API Call', icon: Code },
    { type: 'notification', label: 'Send Notification', icon: Zap },
    { type: 'export', label: 'Export Data', icon: FileText },
    { type: 'transform', label: 'Transform Data', icon: Code }
  ];

  const handleAddAction = (action: { type: string; label: string }) => {
    setSelectedActions([...selectedActions, action]);
  };

  const handleRemoveAction = (index: number) => {
    setSelectedActions(selectedActions.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (name && selectedTrigger && selectedActions.length > 0) {
      onSave({
        name,
        description,
        trigger: selectedTrigger,
        actions: selectedActions
      });
      onClose();
    }
  };

  const canProceed = () => {
    if (step === 'basic') return name.trim().length > 0;
    if (step === 'trigger') return selectedTrigger !== null;
    if (step === 'actions') return selectedActions.length > 0;
    return false;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-recipe-card border border-recipe-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-recipe-border">
          <div>
            <h2 className="text-2xl font-bold text-recipe-text">Create Automation</h2>
            <p className="text-sm text-recipe-text-muted mt-1">Build a custom workflow in 3 simple steps</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-recipe-bg rounded-lg transition-colors text-recipe-text-muted hover:text-recipe-text"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-recipe-border bg-recipe-bg/50">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { id: 'basic', label: 'Basic Info', number: 1 },
              { id: 'trigger', label: 'Trigger', number: 2 },
              { id: 'actions', label: 'Actions', number: 3 }
            ].map((s, index) => (
              <div key={s.id} className="flex items-center flex-1">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all
                      ${step === s.id
                        ? 'bg-recipe-accent text-recipe-bg'
                        : 'bg-recipe-border text-recipe-text-muted'
                      }
                    `}
                  >
                    {s.number}
                  </div>
                  <span className={`text-sm font-medium ${step === s.id ? 'text-recipe-text' : 'text-recipe-text-muted'}`}>
                    {s.label}
                  </span>
                </div>
                {index < 2 && (
                  <ArrowRight className="w-4 h-4 text-recipe-border mx-2 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Basic Info */}
          {step === 'basic' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className="block text-sm font-medium text-recipe-text mb-2">
                  Automation Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Daily Report Generator"
                  className="w-full px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-recipe-text mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this automation does..."
                  rows={4}
                  className="w-full px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 resize-none"
                />
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-400 mb-2">Getting Started</h4>
                <p className="text-sm text-recipe-text-muted">
                  Automations help you streamline workflows by connecting triggers with actions. Start by giving your automation a clear, descriptive name.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Select Trigger */}
          {step === 'trigger' && (
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-recipe-text mb-2">Choose a Trigger</h3>
                <p className="text-sm text-recipe-text-muted">Select what starts your automation</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {triggers.map((trigger) => {
                  const Icon = trigger.icon;
                  const isSelected = selectedTrigger?.type === trigger.type;
                  return (
                    <button
                      key={trigger.type}
                      onClick={() => setSelectedTrigger({ type: trigger.type, label: trigger.label })}
                      className={`
                        p-4 rounded-lg border-2 transition-all text-left
                        ${isSelected
                          ? 'border-recipe-accent bg-recipe-accent/10'
                          : 'border-recipe-border hover:border-recipe-accent/50 hover:bg-recipe-bg'
                        }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-recipe-accent text-recipe-bg' : 'bg-recipe-bg text-recipe-accent'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-semibold ${isSelected ? 'text-recipe-accent' : 'text-recipe-text'}`}>
                            {trigger.label}
                          </h4>
                          <p className="text-xs text-recipe-text-muted mt-1">{trigger.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedTrigger && (
                <div className="mt-6 bg-recipe-bg border border-recipe-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-recipe-text mb-3">Trigger Configuration</h4>
                  {selectedTrigger.type === 'schedule' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-recipe-text-muted mb-2">Frequency</label>
                        <select className="w-full px-3 py-2 bg-recipe-card border border-recipe-border rounded text-sm text-recipe-text">
                          <option>Every Hour</option>
                          <option>Daily</option>
                          <option>Weekly</option>
                          <option>Monthly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-recipe-text-muted mb-2">Time</label>
                        <input type="time" className="w-full px-3 py-2 bg-recipe-card border border-recipe-border rounded text-sm text-recipe-text" />
                      </div>
                    </div>
                  )}
                  {selectedTrigger.type === 'event' && (
                    <div>
                      <label className="block text-xs text-recipe-text-muted mb-2">Event Type</label>
                      <select className="w-full px-3 py-2 bg-recipe-card border border-recipe-border rounded text-sm text-recipe-text">
                        <option>App Launched</option>
                        <option>New User Created</option>
                        <option>Data Updated</option>
                        <option>Error Occurred</option>
                      </select>
                    </div>
                  )}
                  {selectedTrigger.type === 'webhook' && (
                    <div>
                      <label className="block text-xs text-recipe-text-muted mb-2">Webhook URL</label>
                      <input
                        type="text"
                        placeholder="https://api.example.com/webhook"
                        className="w-full px-3 py-2 bg-recipe-card border border-recipe-border rounded text-sm text-recipe-text font-mono"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Actions */}
          {step === 'actions' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-recipe-text mb-2">Add Actions</h3>
                <p className="text-sm text-recipe-text-muted">Choose what happens when the trigger fires</p>
              </div>

              {/* Selected Actions */}
              {selectedActions.length > 0 && (
                <div className="bg-recipe-bg border border-recipe-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-recipe-text mb-3">
                    Selected Actions ({selectedActions.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedActions.map((action, index) => {
                      const ActionIcon = availableActions.find(a => a.type === action.type)?.icon || Code;
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-recipe-card border border-recipe-border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-semibold text-recipe-accent bg-recipe-accent/10 w-6 h-6 rounded-full flex items-center justify-center">
                              {index + 1}
                            </span>
                            <ActionIcon className="w-4 h-4 text-recipe-accent" />
                            <span className="text-sm text-recipe-text font-medium">{action.label}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveAction(index)}
                            className="p-1 hover:bg-recipe-bg rounded text-recipe-text-muted hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available Actions */}
              <div>
                <h4 className="text-sm font-semibold text-recipe-text mb-3">Available Actions</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.type}
                        onClick={() => handleAddAction({ type: action.type, label: action.label })}
                        className="p-3 border border-recipe-border rounded-lg hover:border-recipe-accent/50 hover:bg-recipe-bg transition-all group"
                      >
                        <Icon className="w-5 h-5 text-recipe-accent mb-2" />
                        <p className="text-xs text-recipe-text font-medium">{action.label}</p>
                        <Plus className="w-3 h-3 text-recipe-text-muted mx-auto mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-recipe-border flex items-center justify-between">
          <button
            onClick={() => {
              if (step === 'basic') {
                onClose();
              } else if (step === 'trigger') {
                setStep('basic');
              } else {
                setStep('trigger');
              }
            }}
            className="px-4 py-2 text-recipe-text-muted hover:text-recipe-text transition-colors"
          >
            {step === 'basic' ? 'Cancel' : 'Back'}
          </button>

          <div className="flex gap-3">
            {step !== 'actions' ? (
              <button
                onClick={() => {
                  if (step === 'basic') setStep('trigger');
                  else if (step === 'trigger') setStep('actions');
                }}
                disabled={!canProceed()}
                className={`
                  px-6 py-2 rounded-lg font-medium transition-all
                  ${canProceed()
                    ? 'bg-recipe-accent text-recipe-bg hover:bg-recipe-accent/90'
                    : 'bg-recipe-border text-recipe-text-muted cursor-not-allowed'
                  }
                `}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={!canProceed()}
                className={`
                  px-6 py-2 rounded-lg font-medium transition-all
                  ${canProceed()
                    ? 'bg-recipe-accent text-recipe-bg hover:bg-recipe-accent/90'
                    : 'bg-recipe-border text-recipe-text-muted cursor-not-allowed'
                  }
                `}
              >
                Create Automation
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
